import React, { useState, useEffect, useCallback, useContext } from 'react';
import AuthContext from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import {
  Box, Typography, Paper, IconButton, CircularProgress, Alert, Divider, Tabs, Tab,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalCafeOutlinedIcon from '@mui/icons-material/LocalCafeOutlined';

import { NAV_BG } from '../theme';
import { elUpperCase } from '../utils/text';
const PRIMARY = '#6C63FF';
const GRAY = '#94a3b8';

function getMondayOf(d) {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

function getWeekDates(monday) {
  return Array.from({ length: 5 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function weekRangeLabel(monday) {
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  const lang = i18n.language === 'el' ? 'el-GR' : 'en-US';
  const startStr = monday.toLocaleDateString(lang, { day: 'numeric', month: 'short' });
  const endStr = friday.toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${startStr} – ${endStr}`;
}

function dayLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(i18n.language === 'el' ? 'el-GR' : 'en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

function MealRow({ icon, label, slot, noMealLabel }) {
  const items = slot ? [slot.choice1, slot.choice2, slot.choice3].filter(Boolean) : [];
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
      <Box sx={{ color: PRIMARY, mt: '2px', display: 'flex' }}>{icon}</Box>
      <Box>
        <Typography sx={{
          fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', fontWeight: 600,
          color: GRAY, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.25,
        }}>
          {elUpperCase(label)}
        </Typography>
        {items.length > 0 ? (
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.9rem', color: NAV_BG }}>
            {items.join(' · ')}
          </Typography>
        ) : (
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#cbd5e1', fontStyle: 'italic' }}>
            {noMealLabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function ParentMenuPage() {
  const { authTokens, backendUrl } = useContext(AuthContext);
  const { t } = useTranslation();

  const [weekStart, setWeekStart] = useState(() => getMondayOf(new Date()));
  // [{child_id, child_name, dayMap: {date: dayObj}}]
  const [children, setChildren] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const authHeader = { 'Content-Type': 'application/json', Authorization: 'Bearer ' + authTokens?.access };

  const weekDates = getWeekDates(weekStart);
  const weekDateStrs = weekDates.map(toDateStr);
  const months = [...new Set(weekDates.map(d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`))];

  const fetchMenu = useCallback(() => {
    setLoading(true);
    setError('');
    Promise.all(
      months.map(m =>
        fetch(backendUrl + `api/parent/menu/?month=${m}`, { headers: authHeader })
          .then(r => r.ok ? r.json() : [])
      )
    )
      .then(results => {
        // Merge per-child results across months: [{child_id, child_name, menu:[...]}]
        const childMap = {};
        results.flat().forEach(c => {
          if (!childMap[c.child_id]) {
            childMap[c.child_id] = { child_id: c.child_id, child_name: c.child_name, dayMap: {} };
          }
          (c.menu || []).forEach(d => { childMap[c.child_id].dayMap[d.date] = d; });
        });
        const list = Object.values(childMap);
        setChildren(list);
        setSelectedIdx(idx => (idx >= list.length ? 0 : idx));
        setLoading(false);
      })
      .catch(() => { setError(t('parent_menu_page.load_error')); setLoading(false); });
  }, [weekStart, authTokens]); // eslint-disable-line

  useEffect(() => { fetchMenu(); }, [fetchMenu]);

  const dayMap = children[selectedIdx]?.dayMap ?? {};

  function prevWeek() {
    setWeekStart(w => { const d = new Date(w); d.setDate(d.getDate() - 7); return d; });
  }

  function nextWeek() {
    setWeekStart(w => { const d = new Date(w); d.setDate(d.getDate() + 7); return d; });
  }

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
      {/* Week navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={prevWeek} size="small" sx={{ color: NAV_BG }}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography sx={{
          fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.1rem',
          color: NAV_BG, flex: 1, textAlign: 'center',
        }}>
          {weekRangeLabel(weekStart)}
        </Typography>
        <IconButton onClick={nextWeek} size="small" sx={{ color: NAV_BG }}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Child tabs — only shown when there are multiple children */}
      {!loading && children.length > 1 && (
        <Tabs
          value={selectedIdx}
          onChange={(_, v) => setSelectedIdx(v)}
          sx={{ mb: 2, borderBottom: '1px solid #e2e8f0', minHeight: 40 }}
          TabIndicatorProps={{ style: { backgroundColor: PRIMARY } }}
        >
          {children.map((c, i) => (
            <Tab
              key={c.child_id}
              label={c.child_name}
              value={i}
              sx={{
                fontFamily: '"Outfit", sans-serif',
                fontWeight: 600,
                fontSize: '0.9rem',
                textTransform: 'none',
                color: selectedIdx === i ? PRIMARY : '#64748b',
                minHeight: 40,
              }}
            />
          ))}
        </Tabs>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress sx={{ color: PRIMARY }} />
        </Box>
      )}

      {!loading && error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

      {!loading && !error && weekDateStrs.map(dateStr => {
        const day = dayMap[dateStr];
        return (
          <Paper key={dateStr} sx={{ mb: 2, borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, py: 1.25, background: NAV_BG }}>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: 'white', fontSize: '0.9rem' }}>
                {dayLabel(dateStr)}
              </Typography>
            </Box>
            <Box sx={{ px: 2.5, py: 2 }}>
              <MealRow
                icon={<WbSunnyOutlinedIcon sx={{ fontSize: 18 }} />}
                label={t('parent_menu_page.breakfast')}
                slot={day?.breakfast ?? null}
                noMealLabel={t('parent_menu_page.no_meal')}
              />
              <Divider sx={{ my: 1 }} />
              <MealRow
                icon={<RestaurantIcon sx={{ fontSize: 18 }} />}
                label={t('parent_menu_page.lunch')}
                slot={day?.lunch ?? null}
                noMealLabel={t('parent_menu_page.no_meal')}
              />
              <Divider sx={{ my: 1 }} />
              <MealRow
                icon={<LocalCafeOutlinedIcon sx={{ fontSize: 18 }} />}
                label={t('parent_menu_page.afternoon')}
                slot={day?.afternoon ?? null}
                noMealLabel={t('parent_menu_page.no_meal')}
              />
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
}
